import type { ButtonProps } from '@mui/material';
import { Box, Button, alpha } from '@mui/material';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import type { LinkProps } from 'react-router';
import { Link, Route, Routes } from 'react-router';
import { useRelativeMatch } from '@/hooks/useRelativeMatch.ts';
import { ArrowRightIcon } from './icons';
import ToggleArrowsUpAndDown from './ToggleArrowsUpAndDown';

export type ActionBoxBaseProps = {
    title: ReactNode;
    subTitle?: ReactNode;
    /**
     * Is this a Call To Action box? The background color will be Primary with white text, otherwise the box
     * is displayed with a gray border and Primary-color text.
     */
    cta?: boolean;
    disabled?: boolean;

    children?: ReactNode;
    showChildren?: boolean;
    /**
     * Removes padding for the children
     */
    disablePadding?: boolean;
};
export type ActionBoxProps = ActionBoxBaseProps &
    (
        | {
              /**
               * Clicking the box will take you to this page. The recommendation is that this page is a sub-page of the current
               * page with back-navigation to take you back to the previous page.
               */
              linkTo: LinkProps['to'];
              href?: never;
              onClick?: LinkProps['onClick'];
          }
        | {
              linkTo?: never;
              href?: never;
              onClick?: ButtonProps<'a'>['onClick'];
          }
        | {
              href: string;
              target?: string;
              linkTo?: never;
              onClick?: never;
          }
    );

export const ActionBox = (props: ActionBoxProps) => {
    const {
        showChildren,
        children,
        title,
        subTitle,
        cta = false,
        linkTo,
        disablePadding,
        ...buttonProps
    } = props;
    const arrowIconSize = 12;
    return (
        <Box
            sx={(theme) => ({
                border: showChildren
                    ? `solid 1px ${theme.bino.color.lightGray}`
                    : 'none',
                borderRadius: '4px',
                backgroundColor: theme.palette.background.paper,
            })}
        >
            <Button
                //variant={cta && theme.palette.mode !== 'dark' ? 'contained' : 'outlined'}
                variant={cta ? 'contained' : 'outlined'}
                {...buttonProps}
                disabled={props.disabled}
                fullWidth
                to={linkTo}
                component={linkTo ? Link : undefined}
                endIcon={
                    !children ? (
                        <ArrowRightIcon size={arrowIconSize} />
                    ) : (
                        <ToggleArrowsUpAndDown toggle={showChildren} />
                    )
                }
                sx={(theme) => ({
                    justifyContent: 'space-between',
                    p: {
                        xs: 1,
                        sm: 2,
                        md: 3,
                    },
                    backgroundColor:
                        theme.palette.mode !== 'dark'
                            ? undefined
                            : alpha(
                                  theme.bino.color.primary,
                                  cta ? 0.75 : 0.15,
                              ),
                    color:
                        theme.palette.mode === 'dark'
                            ? theme.palette.text.primary
                            : undefined,
                    height: 'auto',
                    borderColor:
                        showChildren || theme.palette.mode === 'dark'
                            ? 'transparent'
                            : theme.bino.color.lightGray,
                    textAlign: 'left',
                    '&:hover': cta
                        ? {
                              backgroundColor:
                                  theme.palette.mode !== 'dark'
                                      ? undefined
                                      : alpha(theme.bino.color.primary, 1),
                          }
                        : theme.palette.mode === 'dark'
                          ? {
                                borderColor: 'transparent',
                                background: alpha(
                                    theme.bino.color.primary,
                                    0.25,
                                ),
                            }
                          : {
                                boxShadow: '0px 0px 8px #ccc',
                            },
                })}
            >
                <div>
                    <strong>{title}</strong>
                    {subTitle && (
                        <Box
                            sx={(theme) => ({
                                fontWeight: 'normal',
                                textTransform: 'none',
                                color: cta
                                    ? theme.bino.color.white
                                    : theme.bino.color.palePurple, // Was palePurple but hard to read
                                opacity: 0.8,
                                fontSize: 14,
                                mt: 1,
                            })}
                        >
                            {subTitle}
                        </Box>
                    )}
                </div>
            </Button>
            {showChildren && (
                <Box
                    p={
                        disablePadding
                            ? null
                            : {
                                  xs: 1,
                                  sm: 2,
                                  md: 3,
                              }
                    }
                >
                    {children}
                </Box>
            )}
        </Box>
    );
};

export type ActionBoxRouteProps = Omit<
    ActionBoxBaseProps,
    'children' | 'showChildren'
> & {
    linkTo: string;
    children: ReactNode;
    /**
     * Must be a function wrapped with React.useCallback
     */
    onRouteEnter?: () => void;
    onRouteLeave?: () => void;
};

/**
 * An actionbox that uses routing to open/close the box, and hence expects the parent route to be a splat route <Route path="*" ... />
 *
 * @param props
 * @returns
 */

export const ActionBoxRoute = (props: ActionBoxRouteProps) => {
    const { linkTo, children, onRouteEnter, onRouteLeave, ...actionBoxProps } =
        props;

    const isContentVisible = Boolean(useRelativeMatch('../' + linkTo + '/*'));

    useEffect(() => {
        if (isContentVisible) {
            onRouteEnter?.();
        }
    }, [isContentVisible, onRouteEnter]);

    return (
        <ActionBox
            {...actionBoxProps}
            linkTo={isContentVisible ? '..' : '../' + linkTo}
            onClick={() =>
                isContentVisible ? onRouteEnter?.() : onRouteLeave?.()
            }
            showChildren={isContentVisible}
        >
            <Routes>
                <Route path={linkTo}>
                    <Route index path="*" element={children} />
                </Route>
            </Routes>
        </ActionBox>
    );
};
